import React from 'react'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Switch from '@mui/material/Switch'
import { IOption } from '@user/components/Filters'
import useMediaQuery from '@mui/material/useMediaQuery'
import { getStyles } from '@user/components/Settings/PendingLeadSettings/styles'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Button from '@mui/material/Button'
import { leadTypeImportOption } from '@user/components/Leads/LeadFilters/typings'

const hiddenLeadTypeValues = new Set(
  leadTypeImportOption.map((option) => option.value),
)

type Props = {
  leadTypes: IOption[]
  leadTypesWithoutRule: IOption[]
  autoReviewEnabled: boolean
  selectedTypes: string[]
  handleToggleAutoReview: () => void
  handleManageAssignmentRules: () => void
  handleToggleType: (type: string) => void
}

export const AutoReviewConfigurations = (props: Props) => {
  const {
    leadTypes,
    leadTypesWithoutRule,
    autoReviewEnabled,
    selectedTypes,
    handleToggleAutoReview,
    handleManageAssignmentRules,
    handleToggleType,
  } = props
  const isMobile = useMediaQuery('(max-width:900px)')
  const styles = getStyles(isMobile)
  const autoReviewLeadTypes = leadTypes.filter(
    ({ value }) => !hiddenLeadTypeValues.has(String(value)),
  )

  return (
    <Paper variant="outlined" sx={styles.paper}>
      <Typography variant="subtitle3">Auto-Review Configuration</Typography>

      <Stack
        display="flex"
        direction="row"
        spacing={2}
        p="16px"
        border="1px solid #e2e5ea"
        borderRadius="8px"
        justifyContent="space-between"
        mt="16px"
      >
        <Stack display="flex" direction="row" gap="0 20px" flexWrap="wrap">
          {autoReviewLeadTypes.map(({ label, value }: IOption) => (
            <FormControlLabel
              key={value}
              labelPlacement="end"
              label={
                <Typography
                  variant="body2"
                  sx={{
                    color: !autoReviewEnabled ? 'text.disabled' : 'initial',
                  }}
                >
                  {label}
                </Typography>
              }
              sx={{ marginBottom: 0, marginRight: 0 }}
              data-test-id={`pending-lead-type-${String(value).toLowerCase()}`}
              control={
                <Checkbox
                  onClick={(evt) => evt.stopPropagation()}
                  onChange={() => handleToggleType(String(value))}
                  checked={selectedTypes.includes(String(value))}
                  color="primary"
                  size="small"
                  disabled={!autoReviewEnabled}
                />
              }
            />
          ))}
        </Stack>

        <Switch
          checked={autoReviewEnabled}
          onChange={handleToggleAutoReview}
          size="small"
          data-test-id="pending-lead-auto-review-toggle"
        />
      </Stack>

      {autoReviewEnabled && leadTypesWithoutRule.length > 0 && (
        <Alert
          sx={{
            display: 'flex',
            alignItems: 'start',
            marginTop: '16px',
            '& .MuiAlert-message': { padding: 0 },
          }}
          severity="error"
        >
          <AlertTitle sx={{ mb: 0, mt: 0 }}>
            <Typography variant="subtitle1">
              You must have at least one assignment rule for the lead types
              selected.
            </Typography>
          </AlertTitle>

          <Typography variant="body2">
            You don&#39;t have an assignment rule set up for{' '}
            <b>{leadTypesWithoutRule.map((type) => type.label).join(', ')} </b>
            yet.&nbsp;
            <Button
              sx={{
                color: '#1565C0',
                p: 0,
              }}
              variant="text"
              onClick={handleManageAssignmentRules}
            >
              Manage assignment rules
            </Button>
          </Typography>
        </Alert>
      )}
    </Paper>
  )
}
